import { profileUrl } from '../api/tmdb'
import BrandLogo from './BrandLogo'
import { IconActor, IconActress, IconAny, IconSearch, IconDice, IconArrowRight } from './icons'

/**
 * Shared presentational actor-picker. Pure UI — every screen (Single Player,
 * Multiplayer, Party) feeds its own state + handlers via props and keeps its own
 * data fetching / game logic. Layout is mandated START (left) → arrow → GOAL
 * (right); a single slot renders centered with no arrow.
 */

const PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='185' height='278' viewBox='0 0 185 278'%3E%3Crect fill='%23333' width='185' height='278'/%3E%3Ccircle fill='%23555' cx='92' cy='100' r='40'/%3E%3Cellipse fill='%23555' cx='92' cy='220' rx='60' ry='50'/%3E%3C/svg%3E"

const GENDERS = [
  { value: 'any',    label: 'Any',     Icon: IconAny },
  { value: 'male',   label: 'Actor',   Icon: IconActor },
  { value: 'female', label: 'Actress', Icon: IconActress },
]

function Slot({ role, label, actor, state = 'inactive', onSelect, onClear }) {
  const interactive = typeof onSelect === 'function'
  return (
    <div
      className={`picker-slot picker-slot--${state}${actor ? ' picker-slot--filled' : ''}`}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? `${label}` : undefined}
      onClick={interactive ? onSelect : undefined}
      onKeyDown={interactive ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect() } } : undefined}
    >
      <span className="picker-slot__tag">{role}</span>
      {actor ? (
        <div className="picker-slot__actor">
          <img
            className="picker-slot__avatar"
            src={profileUrl(actor.profile_path) || PLACEHOLDER}
            alt={actor.name}
            onError={(e) => { e.target.src = PLACEHOLDER }}
            draggable={false}
          />
          <span className="picker-slot__name">{actor.name}</span>
          {onClear && (
            <button
              className="picker-slot__clear"
              aria-label="Clear actor"
              onClick={(e) => { e.stopPropagation(); onClear() }}
            >×</button>
          )}
        </div>
      ) : (
        <div className="picker-slot__empty">
          <span className="picker-slot__plus" aria-hidden="true">+</span>
          <span>{label}</span>
        </div>
      )}
    </div>
  )
}

export default function ActorPickerView({
  header = true,
  headline = null,                 // optional centered headline (e.g. party blind pick) instead of the wordmark
  slots = [],
  selectingLabel = null,
  genderFilter = 'any',
  onGenderChange,
  query = '',
  onQueryChange,
  inputRef,
  searching = false,
  results = [],
  onResultPick,
  noResults = false,
  rolling = false,
  onRoll,
  submitLabel = 'Start Game',
  canSubmit = false,
  onSubmit,
  submitHelper,
  extra = null,
  // Optional node rendered below the submit button (e.g. a "Stay on Set"
  // cancel button for party blind picks). Kept out of `extra` because that
  // slot sits between the slots and the search box.
  cancelSlot = null,
}) {
  const single = slots.length === 1

  return (
    <div className="picker">
      {headline ? (
        <p className="picker__headline">{headline}</p>
      ) : header ? (
        <header className="setup__header">
          <h1 className="app-title"><BrandLogo /></h1>
          <p className="app-subtitle">Connect the actors</p>
        </header>
      ) : null}

      <div className={`picker-slots${single ? ' picker-slots--single' : ''}`}>
        {single ? (
          <Slot {...slots[0]} />
        ) : (
          <>
            <Slot {...slots[0]} />
            <span className="picker-slots__arrow" aria-hidden="true"><IconArrowRight size={22} /></span>
            <Slot {...slots[1]} />
          </>
        )}
      </div>

      {extra}

      {selectingLabel && (
        <p className="picker__selecting">Selecting: <strong>{selectingLabel}</strong></p>
      )}

      <div className="picker__search">
        <span className="picker__search-icon" aria-hidden="true"><IconSearch size={18} /></span>
        <input
          ref={inputRef}
          className="picker__search-input"
          type="text"
          placeholder="Search for an actor…"
          value={query}
          onChange={(e) => onQueryChange?.(e.target.value)}
        />
        {query && (
          <button className="picker__search-clear" aria-label="Clear search" onClick={() => onQueryChange?.('')}>×</button>
        )}
      </div>

      {searching && (
        <div className="picker__searching"><div className="spinner" /></div>
      )}

      {results.length > 0 && (
        <div className="picker__results">
          {results.map((actor) => (
            <button key={actor.id} className="picker__result" onClick={() => onResultPick?.(actor)}>
              <img
                src={profileUrl(actor.profile_path, 'w185') || PLACEHOLDER}
                alt={actor.name}
                onError={(e) => { e.target.src = PLACEHOLDER }}
                draggable={false}
              />
              <span>{actor.name}</span>
            </button>
          ))}
        </div>
      )}

      {noResults && (
        <p className="picker__no-results">No actors found for "{query}"</p>
      )}

      <div className="picker__divider"><span>Or Surprise Me</span></div>

      <div className="picker__roll-card">
        <p className="picker__roll-help">Pick a category, then roll the dice. Keep rolling until you like who shows up.</p>
        <div className="picker__gender" role="group" aria-label="Actor category">
          {GENDERS.map(({ value, label, Icon }) => (
            <button
              key={value}
              type="button"
              className={`picker__gender-btn${genderFilter === value ? ' picker__gender-btn--active' : ''}`}
              aria-pressed={genderFilter === value}
              onClick={() => onGenderChange?.(value)}
            >
              <Icon size={16} aria-hidden="true" />
              {label}
            </button>
          ))}
        </div>
        <button type="button" className="picker__roll-btn" onClick={onRoll} disabled={rolling}>
          {rolling ? <div className="spinner spinner--sm" /> : (<><IconDice size={18} aria-hidden="true" /> Roll the Dice</>)}
        </button>
      </div>

      <button className="picker__submit" disabled={!canSubmit} onClick={onSubmit}>
        {submitLabel}
      </button>
      {!canSubmit && submitHelper && (
        <p className="picker__submit-help">{submitHelper}</p>
      )}
      {cancelSlot}
    </div>
  )
}
